<template>
  <v-dialog v-model="dialog" max-width="1200px" persistent scrollable>
    <v-card>
      <v-card-title class="pb-2">
        <span class="headline">
          {{ isEditing ? "Editar Template" : "Novo Template" }}
        </span>
      </v-card-title>

      <v-card-text style="max-height: 70vh">
        <v-alert v-if="error" type="error" dense>{{ error }}</v-alert>

        <v-form ref="form" v-model="valid" @submit.prevent="save">
          <template v-if="!isMetaAccount">
            <v-select
              v-model="accountId"
              :items="accountItems"
              item-text="label"
              item-value="id"
              label="Conta"
              :rules="reqRule"
              :disabled="isEditing"
              outlined
              dense
            />

            <v-select
              v-model="typeKey"
              :items="typeItems"
              item-text="title"
              item-value="key"
              label="Tipo de template"
              :rules="reqRule"
              :disabled="!accountId"
              outlined
              dense
              @change="onTypeChange"
            />
          </template>

          <v-alert
            v-if="requiredVars.length && !isMetaAccount"
            type="info"
            text
            dense
            class="text-body-2"
          >
            Variáveis obrigatórias (use no formato
            <code>{{ mustacheHint }}</code
            >):
            <strong>{{ requiredVars.map((v) => v.name).join(", ") }}</strong>
          </v-alert>

          <!-- ZAPI -->
          <template
            v-if="channel === 'WHATSAPP' && accountType === 'ZAPI_WHATSAPP'"
          >
            <v-textarea
              v-model="zapiText"
              label="Texto da mensagem WhatsApp"
              :rules="reqRule"
              outlined
              auto-grow
              rows="3"
            />
          </template>

          <!-- SMTP -->
          <template v-else-if="channel === 'EMAIL'">
            <v-text-field
              v-model="smtp.subject"
              label="Assunto"
              :rules="reqRule"
              outlined
              dense
            />
            <v-textarea
              v-model="smtp.html"
              label="HTML"
              outlined
              auto-grow
              rows="4"
            />
            <v-textarea
              v-model="smtp.text"
              label="Texto puro (fallback)"
              outlined
              auto-grow
              rows="2"
            />
          </template>

          <!-- META (editor visual) -->
          <template v-else-if="accountType === 'META_WHATSAPP'">
            <template v-if="isMetaAccount && isEditing">
              <div class="mb-2">
                <div class="text-h6 font-weight-medium">{{ typeTitle }}</div>
                <div class="text-caption text--secondary d-flex align-center">
                  <v-icon x-small class="mr-1" color="success"
                    >mdi-whatsapp</v-icon
                  >
                  WhatsApp · Meta
                </div>
              </div>
            </template>
            <meta-template-editor
              ref="metaEditor"
              v-model="metaBody"
              :template-type="selectedType"
            />
          </template>

          <!-- SMS fallback (WhatsApp) -->
          <template v-if="isWhatsAppChannel">
            <v-divider v-if="isMetaAccount" class="my-5" />

            <v-sheet
              :outlined="isMetaAccount"
              :rounded="isMetaAccount"
              :class="isMetaAccount ? 'sms-fallback-panel pa-4' : 'mt-2'"
            >
              <div v-if="isMetaAccount" class="d-flex align-start mb-3">
                <v-avatar
                  color="blue-grey lighten-5"
                  size="36"
                  class="mr-3 flex-shrink-0"
                >
                  <v-icon color="blue-grey darken-2" size="20">
                    mdi-cellphone-message
                  </v-icon>
                </v-avatar>
                <div>
                  <div class="text-subtitle-2 font-weight-medium">
                    SMS (fallback)
                  </div>
                  <div class="text-caption text--secondary">
                    Enviado automaticamente se o WhatsApp falhar
                  </div>
                </div>
              </div>

              <v-textarea
                ref="smsTextarea"
                v-model="smsMessage"
                :label="isMetaAccount ? undefined : 'Mensagem SMS (fallback)'"
                :placeholder="smsPlaceholder"
                :rules="smsRules"
                outlined
                auto-grow
                rows="2"
                :hint="isMetaAccount ? undefined : smsHint"
                :persistent-hint="!isMetaAccount"
                hide-details="auto"
              />
              <div
                v-if="smsVariables.length"
                class="d-flex flex-wrap gap-1 mt-2"
              >
                <v-chip
                  v-for="v in smsVariables"
                  :key="'sms-chip-' + v.name"
                  small
                  class="px-2 var-chip"
                  :color="smsChipColor(v)"
                  :outlined="!smsVarPresent(v.name)"
                  clickable
                  @click="insertSmsVariable(v.name)"
                >
                  {{ formatVar(v.name) }}
                </v-chip>
              </div>
            </v-sheet>
          </template>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close" :disabled="saving">Cancelar</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">
          {{ isEditing ? "Salvar" : "Criar" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import MESSAGER from "@/services/admin/messager.js";
import MetaTemplateEditor from "./MetaTemplateEditor.vue";
import {
  normalizeMetaMetadata,
  buildDefaultMetaMetadata,
} from "@/utils/metaTemplateUtils.js";

const ACCOUNT_CHANNEL = {
  META_WHATSAPP: "WHATSAPP",
  ZAPI_WHATSAPP: "WHATSAPP",
  SMTP_EMAIL: "EMAIL",
};

export default {
  name: "TemplateFormDialog",
  components: { MetaTemplateEditor },
  props: {
    value: { type: Boolean, default: false },
    template: { type: Object, default: null },
    accounts: { type: Array, default: () => [] },
    templateTypes: { type: Array, default: () => [] },
    defaultAccountId: { type: String, default: null },
  },
  data: () => ({
    valid: false,
    saving: false,
    error: null,
    mustacheHint: "{{nome}}",
    accountId: null,
    typeKey: null,
    zapiText: "",
    smsMessage: "",
    smtp: { subject: "", html: "", text: "" },
    metaBody: null,
    reqRule: [(v) => !!v || "Campo obrigatório"],
  }),
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    isEditing() {
      return !!this.template;
    },
    accountItems() {
      return this.accounts
        .filter((a) => a.type !== "META_WHATSAPP")
        .map((a) => ({
          id: a.id,
          label: `${a.type} — ${a.integrationId}`,
          type: a.type,
        }));
    },
    selectedAccount() {
      return this.accounts.find((a) => a.id === this.accountId) || null;
    },
    accountType() {
      if (this.template?.account?.type) return this.template.account.type;
      return this.selectedAccount?.type || null;
    },
    isMetaAccount() {
      return this.accountType === "META_WHATSAPP";
    },
    channel() {
      return this.accountType ? ACCOUNT_CHANNEL[this.accountType] : null;
    },
    isWhatsAppChannel() {
      return this.channel === "WHATSAPP";
    },
    typeItems() {
      if (!this.channel) return [];
      return this.templateTypes.filter((t) => t.channel === this.channel);
    },
    selectedType() {
      return this.templateTypes.find((t) => t.key === this.typeKey) || null;
    },
    requiredVars() {
      return (this.selectedType?.variables || []).filter((v) => v.required);
    },
    smsHint() {
      return "Usada quando o envio WhatsApp falhar";
    },
    smsPlaceholder() {
      return this.selectedType?.defaultSmsMessage || "";
    },
    smsVariables() {
      return (this.selectedType?.variables || []).filter(
        (v) => v.smsAllowed !== false
      );
    },
    smsRequiredMissing() {
      return this.smsVariables
        .filter((v) => v.required)
        .map((v) => v.name)
        .filter((name) => !this.smsVarPresent(name));
    },
    smsRules() {
      return [
        (v) => !!(v && v.trim()) || "Campo obrigatório",
        () =>
          this.smsRequiredMissing.length === 0 ||
          `Inclua: ${this.smsRequiredMissing
            .map((name) => this.formatVar(name))
            .join(", ")}`,
      ];
    },
    typeTitle() {
      return this.selectedType?.title || this.typeKey || "—";
    },
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
  },
  methods: {
    formatVar(name) {
      return `{{${name}}}`;
    },
    smsVarPresent(name) {
      const text = this.smsMessage || "";
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return new RegExp(`{{\\s*${escaped}\\s*}}`).test(text);
    },
    smsChipColor(v) {
      if (this.smsVarPresent(v.name)) return "primary";
      if (v.required) return "error";
      return undefined;
    },
    insertSmsVariable(name) {
      const token = this.formatVar(name);
      const textarea = this.$refs.smsTextarea?.$refs?.input;
      const current = this.smsMessage || "";

      if (textarea && typeof textarea.selectionStart === "number") {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        this.smsMessage = current.slice(0, start) + token + current.slice(end);
        this.$nextTick(() => {
          textarea.focus();
          const pos = start + token.length;
          textarea.setSelectionRange(pos, pos);
        });
      } else {
        this.smsMessage = current + token;
      }
    },
    reset() {
      this.error = null;
      this.zapiText = "";
      this.smsMessage = "";
      this.smtp = { subject: "", html: "", text: "" };
      this.metaBody = null;
      if (this.template) {
        this.accountId = this.template.accountId;
        this.typeKey = this.template.type;
        this.fillMetadata(this.template.metadata);
        this.fillSmsMessage(this.template.body);
      } else {
        this.accountId =
          this.defaultAccountId ||
          (this.accountItems.length === 1 ? this.accountItems[0].id : null);
        this.typeKey = null;
      }
      this.$refs.form && this.$refs.form.resetValidation();
    },
    fillSmsMessage(body) {
      if (body?.sms?.message) {
        this.smsMessage = body.sms.message;
        return;
      }
      this.smsMessage = this.selectedType?.defaultSmsMessage || "";
    },
    fillMetadata(metadata) {
      if (!metadata) return;
      if (this.accountType === "ZAPI_WHATSAPP") {
        this.zapiText = metadata.text || "";
      } else if (this.channel === "EMAIL") {
        this.smtp = {
          subject: metadata.subject || "",
          html: metadata.html || "",
          text: metadata.text || "",
        };
      } else if (this.accountType === "META_WHATSAPP") {
        const type = this.selectedType;
        this.metaBody =
          normalizeMetaMetadata(metadata, type) ||
          buildDefaultMetaMetadata(type);
      }
    },
    onTypeChange() {
      if (this.isEditing) return;
      const def = this.selectedType?.defaultMetadata;
      if (def) this.fillMetadata(def);
      this.smsMessage = this.selectedType?.defaultSmsMessage || "";
    },
    buildMetadata() {
      if (this.accountType === "ZAPI_WHATSAPP") {
        return { text: this.zapiText };
      }
      if (this.channel === "EMAIL") {
        const body = { subject: this.smtp.subject };
        if (this.smtp.html) body.html = this.smtp.html;
        if (this.smtp.text) body.text = this.smtp.text;
        return body;
      }
      if (this.accountType === "META_WHATSAPP") {
        return this.metaBody;
      }
      return {};
    },
    close() {
      this.dialog = false;
    },
    async save() {
      if (
        this.isMetaAccount &&
        this.$refs.metaEditor &&
        !this.$refs.metaEditor.validate()
      ) {
        this.error = "Verifique as configurações do template";
        return;
      }
      if (!this.$refs.form.validate()) return;
      const metadata = this.buildMetadata();
      if (!metadata) {
        this.error = "Dados do template inválidos";
        return;
      }
      try {
        this.saving = true;
        this.error = null;
        const payload = {
          type: this.typeKey,
          metadata,
        };
        if (this.isWhatsAppChannel) payload.smsMessage = this.smsMessage;
        if (this.isEditing) {
          await MESSAGER.templates.update(this.template.id, payload);
        } else {
          await MESSAGER.templates.create({
            accountId: this.accountId,
            type: this.typeKey,
            metadata,
            ...(this.isWhatsAppChannel ? { smsMessage: this.smsMessage } : {}),
          });
        }
        this.close();
        this.$emit("saved");
      } catch (error) {
        this.error = error.message || "Erro ao salvar template";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.gap-1 {
  gap: 4px;
}
.var-chip {
  font-size: 0.8125rem;
  height: 26px;
}
.sms-fallback-panel {
  background: #fafafa;
  border-color: rgba(0, 0, 0, 0.08) !important;
}
</style>
